import { useMemo } from 'react';

import { useI18n } from '@change-corgi/core/react/i18n';
import { useTracking } from '@change-corgi/core/react/tracking';

import {
	useStarterDashboardV2BannerState,
	useStarterDashboardV2PageData,
	useStarterDashboardV2PageFcm,
} from 'src/app/pages/starterDashboardV2/pageContext';
import { isLoading } from 'src/app/shared/utils/async';

import { NOTIFICATION_TYPES } from '../../../../constants';
import { useCreateFreePromotion } from '../useCreateFreePromotion';

type UseFreePromotionNotificationReturn = ModelHookResult<
	{ loading: boolean; impressions: number; amountFormatted: string },
	{ handleCreateFreePromotion: () => Promise<void> }
>;

export function useFreePromotionNotification(): UseFreePromotionNotificationReturn {
	const { localizeCurrency } = useI18n();
	const {
		data: { petition, viewer },
		actions: { refreshData },
	} = useStarterDashboardV2PageData();
	const { pacificPromoFreeSample } = useStarterDashboardV2PageFcm();
	const { actions } = useStarterDashboardV2BannerState();
	const { translate, translateHtml } = useI18n();

	const track = useTracking();

	const { mutate, reset, ...createFreePromotionState } = useCreateFreePromotion({
		onSuccess: () => {
			refreshData();
			void track('free_promo_success');
			actions.addBanner({
				message: translate('corgi.petition.starter_dashboard.notifications.free_promo.response_options.success'),
				severity: 'success',
				dismissable: true,
			});
		},
		onError: () => {
			void track('free_promo_error');
			actions.addBanner({
				message: translateHtml('corgi.petition.starter_dashboard.notifications.free_promo.response_options.error'),
				severity: 'critical',
				dismissable: true,
			});
		},
	});
	const handleCreateFreePromotion = async (): Promise<void> => {
		void track('petition_manage_notification_cta_click', { notification: NOTIFICATION_TYPES.promotion });
		await mutate({ id: petition.id });
	};

	const amount = pacificPromoFreeSample?.amount || 0;
	const impressions = pacificPromoFreeSample?.impressions || 0;
	const viewersCurrencyCode = viewer.user?.country?.currency?.code;

	const amountFormatted = useMemo(() => {
		if (!viewersCurrencyCode) {
			return localizeCurrency(amount, 'USD');
		}

		return localizeCurrency(amount, viewersCurrencyCode);
	}, [localizeCurrency, amount, viewersCurrencyCode]);

	return {
		data: { loading: isLoading(createFreePromotionState), impressions, amountFormatted },
		actions: { handleCreateFreePromotion },
	};
}
